import React, { useState, useEffect } from 'react';
import { flushSync } from 'react-dom';
import axios from 'axios';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { API_BASE_URL } from '../config/config';
import TermsModal from '../components/TermsModal';
import AuthLayout from '../components/AuthLayout';
import useFieldErrors from '../utils/useFieldErrors';
import FieldError from '../components/FieldError';
// 본인인증 업체는 연락처를 01043335746 으로 준다. 관리자(center_admin_info)의 기존
// 번호는 전부 010-4333-5746 형식이라(2026-09-17 운영 확인) 그 형식으로 맞춘다.
// ⚠️ 보호자·대상자 쪽은 건드리지 않는다 — 그 두 표는 하이픈 없는 값으로 저장돼 있고,
//    보호자 비밀번호 찾기가 저장된 값과 인증 값을 글자 그대로 비교한다(server.js:2513).
import formatMobile from '../utils/phone';

const Signup = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const KCB_CERT_URL = process.env.REACT_APP_KCB_CERT_URL;

    // KCB 인증 결과를 받기 위한 전역 함수
    useEffect(() => {
        const handleKcbAuth = (event) => {
            if (event.origin !== API_BASE_URL) return;
            if (event.data?.type === 'KCB_AUTH_RESULT') {
                const { name, tel_no, birthday, di } = event.data.payload;
                setFormData(prev => ({
                    ...prev,
                    user_name: name,
                    user_mobile: formatMobile(tel_no)
                }));
                // 필요시 추가 로직
            }
        };
        window.addEventListener('message', handleKcbAuth);
        return () => window.removeEventListener('message', handleKcbAuth);
    }, []);
    

    // 이메일 인증 페이지에서 전달받은 이메일
    const verifiedEmailFromLocation = location.state?.verifiedEmail || '';

    // 폼 데이터 상태
    const [formData, setFormData] = useState({
        user_name: '',
        user_email: verifiedEmailFromLocation,
        user_pw: '',
        confirmPassword: '',
        user_mobile: '',
    });

    // 지역 및 병원 관련 상태
    const [regions, setRegions] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState('');
    const [selectedDistrict, setSelectedDistrict] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedHospital, setSelectedHospital] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [hospitalSearchTerm, setHospitalSearchTerm] = useState('');
    const [hospitalSearchResults, setHospitalSearchResults] = useState([]);

    // 이메일 검증 관련 상태 추가
    const [isEmailChecking, setIsEmailChecking] = useState(false);
    const [emailCheckResult, setEmailCheckResult] = useState(
        verifiedEmailFromLocation ? '이메일 인증이 완료되었습니다.' : null
    );
    const [isEmailValid, setIsEmailValid] = useState(!!verifiedEmailFromLocation);
    const [isEmailVerified, setIsEmailVerified] = useState(!!verifiedEmailFromLocation);
    const [isSendingVerification, setIsSendingVerification] = useState(false);
    const [isWaitingForVerification, setIsWaitingForVerification] = useState(false);
    const [isVerifyingToken, setIsVerifyingToken] = useState(false);

    // URL에서 토큰 확인 및 인증 처리.
    // 메일함의 인증 링크(/signup?token=…)로 열린 창이다. 이 창은 인증 결과만 알리고
    // 폼은 그리지 않는다 — 링크에 type 이 없어 signupType 이 기본값(보호자)으로 잡히고,
    // 예전에는 안내 아래에 보호자 회원가입 폼이 함께 떴다. 첫 화면부터 폼이 깜박이지
    // 않도록 초기값을 주소에서 바로 읽는다.
    const [tokenInUrl, setTokenInUrl] = useState(() => !!searchParams.get('token'));

    // [1] 인증 결과 상태 추가
    const [kcbAuthResult, setKcbAuthResult] = useState(null);

    // [1] Add state for signup type and guardian/users
    const [signupType, setSignupType] = useState(() => {
        // URL 파라미터에서 type을 확인하여 초기값 설정
        const typeFromUrl = searchParams.get('type');
        return typeFromUrl === 'doctor' ? 'doctor' : 'guardian';
    }); // 'doctor' or 'guardian' - URL 파라미터에 따라 초기값 설정
    const [guardianInfo, setGuardianInfo] = useState({ 
        username: '', 
        password: '', 
        confirmPassword: '', 
        name: '', 
        mobile: '',
        gender: '' // 성별 필드 추가
    });
    const [guardianAuthResult, setGuardianAuthResult] = useState(null);
    const [users, setUsers] = useState([{ name: '', mobile: '', gender: '', authResult: null }]);
    const [selectedUserIndex, setSelectedUserIndex] = useState(0); // 현재 선택된 사용자 인덱스
    
    // 이용약관 동의 관련 상태
    const [agreedTerms, setAgreedTerms] = useState({
        serviceTerms: false,
        privacyPolicy: false,
        refundPolicy: false,
        allAgreed: false
    });
    
    // 모달 상태
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalType, setModalType] = useState('');

    // 입력칸 오류 상태 (spec D7)
    const { errors, replaceErrors, setError, clearError, fieldProps, focusFirstError } = useFieldErrors();
    const [hospitalNotice, setHospitalNotice] = useState('');

    // SHA-256 암호화 함수 (CI값용)
    const sha256 = async (message) => {
        // TextEncoder를 사용하여 문자열을 Uint8Array로 변환
        const msgBuffer = new TextEncoder().encode(message);
        
        // SubtleCrypto API를 사용하여 SHA-256 해시 생성
        const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
        
        // ArrayBuffer를 Uint8Array로 변환
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        
        // 각 바이트를 16진수 문자열로 변환하고 연결
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        
        return hashHex;
    };

    // CI값 암호화 함수
    const encryptCI = async (ci) => {
        try {
            const hashedCI = await sha256(ci);
            return hashedCI;
        } catch (error) {
            console.error('CI값 암호화 오류:', error);
            throw new Error('CI값 암호화에 실패했습니다.');
        }
    };

    // ECI값 처리 함수 (일반 텍스트로 저장)
    const processECI = async (eci) => {
        try {
            return eci; // 일반 텍스트로 반환
        } catch (error) {
            console.error('ECI값 처리 오류:', error);
            throw new Error('ECI값 처리에 실패했습니다.');
        }
    };

    // 비밀번호 암호화 함수 (SHA-256 단방향)
    const encryptPassword = async (password) => {
        try {
            const hashedPassword = await sha256(password);
            return hashedPassword;
        } catch (error) {
            console.error('비밀번호 암호화 오류:', error);
            throw new Error('비밀번호 암호화에 실패했습니다.');
        }
    };

    // 텍스트 암호화 함수 (sdss_cd.enc_text 양방향)
    const encryptText = async (text) => {
        try {
            // sdss_cd.enc_text 함수 호출 (실제 구현에 따라 수정 필요)
            if (window.sdss_cd && window.sdss_cd.enc_text) {
                return window.sdss_cd.enc_text(text);
            } else {
                // 임시로 base64 인코딩 사용 (실제로는 sdss_cd.enc_text 사용)
                return btoa(encodeURIComponent(text));
            }
        } catch (error) {
            console.error('텍스트 암호화 오류:', error);
            throw new Error('텍스트 암호화에 실패했습니다.');
        }
    };

    useEffect(() => {
        const token = searchParams.get('token');
        if (token) {
            setTokenInUrl(true);
            handleEmailTokenVerification(token, false);
        }
    }, [searchParams]);

    // 이메일 토큰 인증 처리
    const handleEmailTokenVerification = async (token, showAlert = true) => {
        setIsVerifyingToken(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/api/verify-email-token`, {
                params: { token }
            });

            if (response.data.success) {
                setEmailCheckResult('이메일 인증이 완료되었습니다.');
                setIsEmailValid(true);
                setIsEmailVerified(true);
                clearError('user_email');
                setFormData(prev => ({ ...prev, user_email: response.data.email }));
                if (showAlert) {
                    alert('이메일 인증이 완료되었습니다!');
                }

                // 이름 입력란에 포커스 및 스크롤 맨 위로 이동
                setTimeout(() => {
                    document.querySelector('input[name="user_name"]')?.focus();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }, 100);

                // 다른 탭에서도 인증 완료를 알리기 위해 localStorage에 기록
                localStorage.setItem('email_verified', JSON.stringify({
                    email: response.data.email,
                    time: Date.now()
                }));

                // 기존 창이 있으면 닫기
                if (window.opener) {
                    window.opener.close();
                }

                // URL에서 토큰 제거
                const newUrl = window.location.pathname;
                window.history.replaceState({}, document.title, newUrl);
            } else {
                setEmailCheckResult(response.data.message || '이메일 인증에 실패했습니다.');
                setIsEmailValid(false);
                setIsEmailVerified(false);
                if (showAlert) {
                    alert(response.data.message || '이메일 인증에 실패했습니다.');
                }
            }
        } catch (error) {
            console.error('이메일 토큰 인증 오류:', error);
            const errorMessage = error.response?.data?.message || '이메일 인증 확인 중 오류가 발생했습니다.';
            setEmailCheckResult(errorMessage);
            setIsEmailValid(false);
            setIsEmailVerified(false);
            if (showAlert) {
                alert(errorMessage);
            }
        } finally {
            setIsVerifyingToken(false);
        }
    };

    // 이메일 인증 상태 폴링
    useEffect(() => {
        let intervalId;

        if (isWaitingForVerification && !isEmailVerified && formData.user_email) {
            intervalId = setInterval(async () => {
                try {
                    const response = await axios.get(`${API_BASE_URL}/api/check-email-verification-status`, {
                        params: { email: formData.user_email }
                    });

                    if (response.data.success && response.data.verified) {
                        setIsEmailVerified(true);
                        setIsEmailValid(true);
                        clearError('user_email');
                        setEmailCheckResult('이메일 인증이 완료되었습니다.');
                        setIsWaitingForVerification(false);
                        clearInterval(intervalId);
                        
                        // 다른 탭과 인증 상태 동기화용
                        localStorage.setItem('email_verified', JSON.stringify({
                            email: formData.user_email,
                            time: Date.now()
                        }));

                        alert('이메일 인증이 완료되었습니다!');
                        
                        // 이름 입력란에 포커스
                        setTimeout(() => {
                            document.querySelector('input[name="user_name"]')?.focus();
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }, 100);
                    }
                } catch (error) {
                    console.error('이메일 인증 상태 확인 오류:', error);
                }
            }, 3000); // 3초마다 확인
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [isWaitingForVerification, isEmailVerified, formData.user_email]);

    // 폼 입력 핸들러
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        clearError(name);
        // 메일 주소를 바꾸면 앞서 받은 검증·발송 결과는 더 이상 이 주소의 것이 아니다.
        if (name === 'user_email' && !isEmailVerified) {
            setIsEmailValid(false);
            setIsWaitingForVerification(false);
            setEmailCheckResult(null);
        }
        // 확인 칸의 불일치 안내는 비밀번호 칸을 고쳐도 더는 맞지 않는 말이 된다.
        if (name === 'user_pw') clearError('confirmPassword');
    };

    // 컴포넌트 마운트 시 지역 목록 가져오기
    useEffect(() => {
        fetchRegions();
    }, []);

    // 지역 목록 가져오기
    const fetchRegions = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/regions`);
            if (response.data.success) {
                setRegions(response.data.regions);
            }
        } catch (error) {
            console.error('지역 목록 가져오기 실패:', error);
        }
    };

    // 지역 선택 핸들러
    const handleRegionChange = async (e) => {
        clearError('region');
        setHospitalNotice('');
        const region = e.target.value;
        setSelectedRegion(region);
        setSelectedDistrict('');
        setSearchResults([]);
        setSearchTerm('');
        setSelectedHospital('');

        if (region) {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/districts/${encodeURIComponent(region)}`);
                if (response.data.success) {
                    setDistricts(response.data.districts);
                }
            } catch (error) {
                console.error('구/군 목록 가져오기 실패:', error);
                setDistricts([]);
            }
        } else {
            setDistricts([]);
        }
    };

    // 구/군 선택 핸들러
    const handleDistrictChange = (e) => {
        setSelectedDistrict(e.target.value);
        setSearchResults([]);
        setSearchTerm('');
        setSelectedHospital('');
    };

    // 병원 검색 핸들러
    const handleHospitalSearch = async () => {
        try {
            setHospitalNotice('');
            if (!selectedRegion) {
                setError('region', '지역을 선택해주세요.');
                focusFirstError({ region: true });
                return;
            }
            clearError('region');

            const response = await axios.get(`${API_BASE_URL}/api/hospitals/search`, {
                params: {
                    region_name: selectedRegion,
                    district_name: selectedDistrict,
                    search_term: hospitalSearchTerm
                }
            });

            if (response.data.success) {
                if (response.data.hospitals.length === 0) {
                    setHospitalNotice('검색 결과가 없습니다.');
                }
                setHospitalSearchResults(response.data.hospitals);
            } else {
                setHospitalSearchResults([]);
                alert('병원 검색에 실패했습니다.');
            }
        } catch (error) {
            console.error('병원 검색 오류:', error);
            setHospitalSearchResults([]);
            if (error.response) {
                alert(error.response.data.message || '병원 검색 중 오류가 발생했습니다.');
            } else {
                alert('서버와의 통신 중 오류가 발생했습니다.');
            }
        }
    };

    // 병원 선택 핸들러
    const handleHospitalSelect = (hospital) => {
        setSelectedHospital(hospital);
        setHospitalSearchTerm(hospital.institution_name);
        setHospitalSearchResults([]); // 검색 결과 초기화
    };

    // 이메일 검증 핸들러
    // 메일 검증: 주소 확인(중복·도메인)을 통과하면 인증 링크 메일까지 한 번에 보낸다.
    // 예전에는 "메일 검증" 뒤에 "인증 링크 전송"을 따로 눌러야 했다.
    const handleEmailVerification = async () => {
        if (!formData.user_email) {
            setError('user_email', '이메일을 입력해주세요.');
            focusFirstError({ user_email: true });
            return;
        }

        setIsEmailChecking(true);
        let canSend = false;
        try {
            const response = await axios.post(`${API_BASE_URL}/api/verify-email`, {
                user_email: formData.user_email
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                canSend = response.data.isValid && !response.data.isDuplicate;
                setEmailCheckResult(response.data.message);
                setIsEmailValid(canSend);
            } else {
                setEmailCheckResult(response.data.message || '이메일 검증에 실패했습니다.');
                setIsEmailValid(false);
            }
        } catch (error) {
            console.error('이메일 검증 오류:', error);
            console.error('오류 상세 정보:', error.response?.data);
            setEmailCheckResult(error.response?.data?.message || '이메일 검증 중 오류가 발생했습니다.');
            setIsEmailValid(false);
        } finally {
            setIsEmailChecking(false);
        }

        if (canSend) {
            await sendVerificationEmail();
        }
    };

    // 이메일 인증 링크 전송 핸들러
    const handleSendVerificationEmail = async () => {
        if (!formData.user_email) {
            setError('user_email', '이메일을 입력해주세요.');
            focusFirstError({ user_email: true });
            return;
        }

        if (!isEmailValid) {
            setError('user_email', '먼저 이메일 검증을 진행해주세요.');
            focusFirstError({ user_email: true });
            return;
        }

        await sendVerificationEmail();
    };

    // 인증 링크 메일 발송. 검증 직후(isEmailValid 상태가 아직 반영되기 전)에도 부르므로
    // 상태 검사는 부르는 쪽이 한다.
    const sendVerificationEmail = async () => {
        setIsSendingVerification(true);
        try {
            const response = await axios.post(`${API_BASE_URL}/api/send-verification-email`, {
                user_email: formData.user_email,
                clientOrigin: window.location.origin
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                alert('인증 이메일이 전송되었습니다. 이메일을 확인해주세요.');
                setEmailCheckResult('인증 이메일이 전송되었습니다. 이메일을 확인해주세요. (인증 대기 중...)');
                setIsWaitingForVerification(true);
            } else {
                alert(response.data.message || '인증 이메일 전송에 실패했습니다.');
                setEmailCheckResult(response.data.message || '인증 이메일 전송에 실패했습니다.');
            }
        } catch (error) {
            console.error('인증 이메일 전송 오류:', error);
            const errorMessage = error.response?.data?.message || '인증 이메일 전송 중 오류가 발생했습니다.';
            alert(errorMessage);
            setEmailCheckResult(errorMessage);
        } finally {
            setIsSendingVerification(false);
        }
    };

    // 회원가입 제출 핸들러
    const handleSubmit = async (e) => {
        e.preventDefault();

        // spec D7: 입력칸에 관한 안내는 알림창이 아니라 그 칸 아래에 보인다. 한 번에 찾은 것을 모두 보이고 첫 칸으로 간다.
        const next = {};
        if (!isEmailVerified) next.user_email = '이메일 인증을 진행해주세요.';
        if (formData.user_pw !== formData.confirmPassword) next.confirmPassword = '비밀번호가 일치하지 않습니다.';
        if (Object.keys(next).length > 0) {
            replaceErrors(next);
            focusFirstError(next);
            return;
        }
        replaceErrors({});

        try {
            // CI값이 있는 경우에만 암호화
            let encryptedCI = null;
            if (kcbAuthResult && kcbAuthResult.di) {
                encryptedCI = await encryptCI(kcbAuthResult.di);
            }

            const response = await axios.post(`${API_BASE_URL}/api/signup`, {
                ...formData,
                hospital: selectedHospital,
                region: selectedRegion,
                ci_encrypted: encryptedCI // 암호화된 CI값 (있는 경우에만)
            });

            if (response.data.success) {
                alert('회원가입이 완료되었습니다.');
                navigate('/login');
            }
        } catch (error) {
            console.error('회원가입 실패:', error);
            if (error.response?.status === 409) {
                // 이미 가입된 이메일. 입력칸에 관한 안내라 알림창 대신 이메일 칸 아래에 보인다(D7).
                const next = { user_email: error.response.data?.message || '이미 사용 중인 이메일입니다.' };
                replaceErrors(next);
                focusFirstError(next);
            } else if (error.message.includes('CI값 암호화')) {
                alert('CI값 암호화 중 오류가 발생했습니다. 다시 시도해주세요.');
            } else {
                alert('회원가입 중 오류가 발생했습니다.');
            }
        }
    };

    useEffect(() => {
        const handleStorage = (event) => {
            if (event.key === 'email_verified') {
                try {
                    const data = JSON.parse(event.newValue);
                    if (data && data.email) {
                        setIsEmailValid(true);
                        setIsEmailVerified(true);
                        clearError('user_email');
                        setFormData(prev => ({ ...prev, user_email: data.email }));
                        setEmailCheckResult('이메일 인증이 완료되었습니다.');
                        alert('이메일 인증이 완료되었습니다!');
                        // 이름 입력란에 포커스 및 스크롤 맨 위로 이동
                        setTimeout(() => {
                            document.querySelector('input[name="user_name"]')?.focus();
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }, 100);
                    }
                } catch {}
            }
        };
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, []);

    useEffect(() => {
        localStorage.setItem('signup_form', JSON.stringify(formData));
    }, [formData]);

    useEffect(() => {
        const saved = localStorage.getItem('signup_form');
        if (saved) {
            setFormData(JSON.parse(saved));
        }
    }, []);

    // 1. postMessage만 남기기
    useEffect(() => {
      const handleMessage = (event) => {
        // console.log('[KCB 인증] 📩 postMessage 수신:', event); // 반드시 로그 남기기
        // console.log('[KCB 인증] 📩 postMessage 상세 정보:', {
        //   origin: event.origin,
        //   data: event.data,
        //   source: event.source,
        //   lastEventId: event.lastEventId,
        //   type: event.type,
        //   timeStamp: event.timeStamp,
        //   bubbles: event.bubbles,
        //   cancelable: event.cancelable,
        //   defaultPrevented: event.defaultPrevented,
        //   eventPhase: event.eventPhase,
        //   isTrusted: event.isTrusted,
        //   target: event.target,
        //   currentTarget: event.currentTarget
        // });
        
        // [B] 추가: event.origin, event.data 로그
        // console.log('[KCB DEBUG] event.origin:', event.origin);
        // console.log('[KCB DEBUG] event.data:', event.data);
        // console.log('[KCB DEBUG] event.data의 모든 키:', Object.keys(event.data));
        // console.log('[KCB DEBUG] event.data의 모든 값:', Object.values(event.data));
        
        // origin 체크 전에 현재 window.location 정보 로그
        // console.log('[KCB DEBUG] 현재 window.location 정보:', {
        //   origin: window.location.origin,
        //   href: window.location.href,
        //   protocol: window.location.protocol,
        //   hostname: window.location.hostname,
        //   port: window.location.port
        // });
        
        if (event.origin !== window.location.origin) {  // 하드코딩된 origin 대신 실제 부모창의 origin 사용
          // console.warn('[KCB WARNING] event.origin 불일치:', {
          //   receivedOrigin: event.origin,
          //   expectedOrigin: window.location.origin,
          //   difference: event.origin !== window.location.origin
          // });
          return;
        }

        // payload 없이 바로 데이터가 온다면 (KCB쪽에서 key명이 다를 수도 있으니 name도 같이 확인)
        const { rslt_name, tel_no, di, birthday, sex_cd, name, authType, userIndex, eci } = event.data;
        
        // console.log('[KCB DEBUG] rslt_name:', rslt_name);
        // console.log('[KCB DEBUG] tel_no:', tel_no);
        // console.log('[KCB DEBUG] birthday:', birthday);
        // console.log('[KCB DEBUG] sex_cd:', sex_cd);
        // console.log('[KCB DEBUG] authType:', authType);
        // console.log('[KCB DEBUG] userIndex:', userIndex);
        // console.log('[KCB DEBUG] eci:', eci);
        // console.log('[KCB DEBUG] di:', di);
        
        // 보호자 인증 결과 처리
        if (authType === 'guardian' && rslt_name && tel_no) {
          // console.log('[KCB DEBUG] 보호자 인증 결과 처리 시작');
          setGuardianAuthResult({ name: rslt_name, mobile: tel_no, birthday: birthday, sex_cd: sex_cd, eci: eci });
          setGuardianInfo(prev => ({
            ...prev,
            name: rslt_name,
            mobile: tel_no
          }));
          clearError('guardian_auth');
          clearError('guardian_name');
          clearError('guardian_mobile');
          // console.log('[KCB DEBUG] 보호자 인증 결과 처리 완료');
          return;
        }

        // 사용자 인증 결과 처리
        if (authType === 'user' && userIndex !== undefined && rslt_name && tel_no) {
          // console.log('[KCB DEBUG] 사용자 인증 결과 처리 시작, userIndex:', userIndex);
          const newUsers = [...users];
          newUsers[userIndex] = {
            ...newUsers[userIndex],
            name: rslt_name,
            mobile: tel_no,
            authResult: { name: rslt_name, mobile: tel_no, birthday: birthday, sex_cd: sex_cd, eci: eci }
          };
          setUsers(newUsers);
          clearError('user_0');
          // console.log('[KCB DEBUG] 사용자 인증 결과 처리 완료');
          return;
        }
        
        // 기존 방식 지원 (authType이 없는 경우)
        if (rslt_name && tel_no) {
          // console.log('[KCB DEBUG] 기존 방식 처리 시작');
          
          // 보호자 회원가입 모드이고 authType이 없으면 보호자 정보에 입력
          if (signupType === 'guardian') {
            // console.log('[KCB DEBUG] 보호자 모드에서 기존 방식 처리');
            
            // 현재 인증 중인 사용자 인덱스가 있으면 사용자 정보에 입력
            if (window.currentUserIndex !== undefined) {
              // console.log('[KCB DEBUG] 사용자 인증 결과 처리, userIndex:', window.currentUserIndex);
              const idx = window.currentUserIndex;
              setUsers(prevUsers => {
                const updated = [...prevUsers];
                updated[idx] = {
                  ...updated[idx],
                  name: rslt_name,
                  mobile: tel_no,
                  birthdate: birthday,  // 생년월일 추가
                  gender: sex_cd,       // 성별 추가
                  authResult: { name: rslt_name, mobile: tel_no, birthday: birthday, sex_cd: sex_cd, eci: eci }
                };
                return updated;
              });
              window.currentUserIndex = undefined; // 초기화
              clearError('user_0');
              clearError(`user_${idx}_gender`);
              // console.log('[KCB DEBUG] 사용자 인증 결과 처리 완료');
              return;
            }

            // 사용자 인덱스가 없으면 보호자 정보에 입력
            // console.log('[KCB DEBUG] 보호자 정보에 입력');
            setGuardianAuthResult({ name: rslt_name, mobile: tel_no, birthday: birthday, sex_cd: sex_cd, eci: eci });
            setGuardianInfo(prev => ({
              ...prev,
              name: rslt_name,
              mobile: tel_no,
              birthdate: birthday,  // 생년월일 추가
              gender: sex_cd        // 성별 추가
            }));
            clearError('guardian_auth');
            clearError('guardian_name');
            clearError('guardian_mobile');
            clearError('guardian_gender');
            // console.log('[KCB DEBUG] 보호자 정보 입력 완료');
            return;
          }
          
          // 주치의 회원가입 모드면 기존 방식대로 처리
          // console.log('[KCB DEBUG] 주치의 모드에서 기존 방식 처리');
          setFormData(prev => ({
            ...prev,
            user_name: rslt_name,
            user_mobile: formatMobile(tel_no),
            // 필요하면 birthday, di 등도 추가
          }));
          // console.log('[KCB DEBUG] 주치의 정보 입력 완료');
          return;
        }

        // 혹시 기존 방식도 같이 지원
        if (event.data?.type === 'KCB_AUTH_RESULT' && event.data?.payload) {
          // console.log('[KCB DEBUG] payload 방식 처리 시작');
          const { name, tel_no, birthday, di } = event.data.payload;
          setFormData(prev => ({
            ...prev,
            user_name: name,
            user_mobile: formatMobile(tel_no)
          }));
          // console.log('[KCB DEBUG] payload 방식 처리 완료');
          return;
        }
        
        // console.log('[KCB DEBUG] 처리되지 않은 postMessage 데이터:', event.data);
      };
      window.addEventListener('message', handleMessage);
      // fallback: localStorage polling
      const timer = setInterval(() => {
        const saved = localStorage.getItem('kcb_auth_result');
        if (saved) {
          try {
            const data = JSON.parse(saved);
            
            // 보호자 인증 결과 처리
            if (data.authType === 'guardian' && data.rslt_name && data.tel_no) {
              setGuardianAuthResult({ name: data.rslt_name, mobile: data.tel_no, birthday: data.birthday, sex_cd: data.sex_cd, eci: data.eci });
              setGuardianInfo(prev => ({
                ...prev,
                name: data.rslt_name,
                mobile: data.tel_no,
                birthdate: data.birthday,  // 생년월일 추가
                gender: data.sex_cd        // 성별 추가
              }));
              clearError('guardian_auth');
              clearError('guardian_name');
              clearError('guardian_mobile');
              clearError('guardian_gender');
              localStorage.removeItem('kcb_auth_result');
              clearInterval(timer);
              return;
            }

            // 사용자 인증 결과 처리
            if (data.authType === 'user' && data.userIndex !== undefined && data.rslt_name && data.tel_no) {
              const newUsers = [...users];
              newUsers[data.userIndex] = {
                ...newUsers[data.userIndex],
                name: data.rslt_name,
                mobile: data.tel_no,
                birthdate: data.birthday,  // 생년월일 추가
                gender: data.sex_cd,       // 성별 추가
                authResult: { name: data.rslt_name, mobile: data.tel_no, birthday: data.birthday, sex_cd: data.sex_cd, eci: data.eci }
              };
              setUsers(newUsers);
              clearError('user_0');
              clearError(`user_${data.userIndex}_gender`);
              localStorage.removeItem('kcb_auth_result');
              clearInterval(timer);
              return;
            }
            
            // 기존 방식 지원
            if (data.rslt_name && data.tel_no) {
              setFormData(prev => ({
                ...prev,
                user_name: data.rslt_name,
                user_mobile: formatMobile(data.tel_no),
              }));
              localStorage.removeItem('kcb_auth_result');
              clearInterval(timer);
              return;
            }
            // 기존 방식도 같이 지원
            if (data.name && data.tel_no) {
              setFormData(prev => ({
                ...prev,
                user_name: data.name,
                user_mobile: formatMobile(data.tel_no),
              }));
              localStorage.removeItem('kcb_auth_result');
              clearInterval(timer);
              return;
            }
                      } catch (e) {
              // console.warn('[KCB 인증] [fallback] localStorage 파싱 실패:', e);
            }
        }
      }, 1000);
      return () => {
        window.removeEventListener('message', handleMessage);
        clearInterval(timer);
      };
    }, []);

    // 2. 팝업 열기 (전역 핸들 저장)
    const openKcbCertPopup = () => {
        // 포트 없는 도메인으로만!
        const { protocol, hostname } = window.location;
        const origin = `${protocol}//${hostname}`; // ex: https://sdss-dev.shcl.co.kr

        const redirectUrl = `${origin}/cert/kcb/popup3`;

        // ✅ origin 파라미터 추가!
        const popupUrl =
            `${origin}/auth/popup2?view=web` +
            `&redirectUrl=${encodeURIComponent(redirectUrl)}` +
            `&origin=${encodeURIComponent(origin)}`;

        console.log('[KCB DEBUG] origin:', origin);
        console.log('[KCB DEBUG] redirectUrl:', redirectUrl);
        console.log('[KCB DEBUG] popupUrl:', popupUrl);
        window.kcbPopup = window.open(popupUrl, '_blank', 'width=430,height=640');
        if (!window.kcbPopup || window.kcbPopup.closed) {
            alert('팝업이 차단되었습니다. 팝업 차단을 해제해주세요.');
        }
        console.log('[KCB DEBUG] window.kcbPopup:', window.kcbPopup);
    };

    // 보호자 본인인증 팝업 열기
    const openGuardianKcbCertPopup = () => {
        // console.log('[KCB Guardian] 팝업 열기 시작');
        
        const { protocol, hostname } = window.location;
        const origin = `${protocol}//${hostname}`;
        const redirectUrl = `${origin}/cert/kcb/popup3`;
        const popupUrl =
            `${origin}/auth/popup2?view=web` +
            `&redirectUrl=${encodeURIComponent(redirectUrl)}` +
            `&origin=${encodeURIComponent(origin)}` +
            `&authType=guardian`;

        // console.log('[KCB Guardian] 환경 정보:', {
        //     protocol,
        //     hostname,
        //     origin,
        //     redirectUrl,
        //     popupUrl,
        //     userAgent: navigator.userAgent,
        //     windowWidth: window.innerWidth,
        //     windowHeight: window.innerHeight
        // });

        // console.log('[KCB Guardian] popupUrl:', popupUrl);
        
        try {
            window.guardianKcbPopup = window.open(popupUrl, '_blank', 'width=430,height=640');
            // console.log('[KCB Guardian] 팝업 객체:', window.guardianKcbPopup);
            
            if (!window.guardianKcbPopup) {
                // console.error('[KCB Guardian] 팝업이 차단되었습니다');
                alert('팝업이 차단되었습니다. 팝업 차단을 해제해주세요.');
                return;
            }
            
            if (window.guardianKcbPopup.closed) {
                // console.error('[KCB Guardian] 팝업이 즉시 닫혔습니다');
                alert('팝업이 차단되었습니다. 팝업 차단을 해제해주세요.');
                return;
            }
            
            // console.log('[KCB Guardian] 팝업 열기 성공');
            
            // 팝업 로드 상태 확인
            const checkPopupStatus = setInterval(() => {
                if (window.guardianKcbPopup.closed) {
                    // console.log('[KCB Guardian] 팝업이 닫혔습니다');
                    clearInterval(checkPopupStatus);
                } else {
                    try {
                        // 팝업이 로드되었는지 확인
                        if (window.guardianKcbPopup.location.href) {
                            // console.log('[KCB Guardian] 팝업 URL:', window.guardianKcbPopup.location.href);
                            
                            // CertSelect 페이지가 로드되었는지 확인
                            if (window.guardianKcbPopup.location.href.includes('/auth/popup2')) {
                                // console.log('[KCB Guardian] CertSelect 페이지 로드 완료');
                                clearInterval(checkPopupStatus);
                            }
                            
                            // 팝업이 실제 KCB 인증 페이지로 이동했는지 확인
                            if (window.guardianKcbPopup.location.href.includes('nice.checkplus.co.kr')) {
                                // console.log('[KCB Guardian] KCB 인증 페이지로 이동 완료');
                                clearInterval(checkPopupStatus);
                            }
                        }
                    } catch (e) {
                        // CORS 에러는 정상적인 상황
                        // console.log('[KCB Guardian] 팝업 로딩 중...');
                    }
                }
            }, 1000);
            
            // 30초 후 타임아웃
            setTimeout(() => {
                clearInterval(checkPopupStatus);
                // console.log('[KCB Guardian] 팝업 로딩 타임아웃');
            }, 30000);
            
        } catch (error) {
            // console.error('[KCB Guardian] 팝업 열기 실패:', error);
            alert('팝업을 열 수 없습니다: ' + error.message);
        }
    };

    // 사용자 본인인증 팝업 열기
    const openUserKcbCertPopup = (userIndex) => {
        // console.log('[KCB User] 팝업 열기 시작, userIndex:', userIndex);
        
        const { protocol, hostname } = window.location;
        const origin = `${protocol}//${hostname}`;
        const redirectUrl = `${origin}/cert/kcb/popup3`;
        const popupUrl =
            `${origin}/auth/popup2?view=web` +
            `&redirectUrl=${encodeURIComponent(redirectUrl)}` +
            `&origin=${encodeURIComponent(origin)}` +
            `&authType=user&userIndex=${userIndex}`;

        // console.log('[KCB User] 환경 정보:', {
        //     protocol,
        //     hostname,
        //     origin,
        //     redirectUrl,
        //     popupUrl,
        //     userIndex,
        //     userAgent: navigator.userAgent,
        //     windowWidth: window.innerWidth,
        //     windowHeight: window.innerHeight
        // });

        // console.log('[KCB User] popupUrl:', popupUrl);
        
        // 현재 인증 중인 사용자 인덱스를 저장
        window.currentUserIndex = userIndex;
        
        try {
            window.userKcbPopup = window.open(popupUrl, '_blank', 'width=430,height=640');
            // console.log('[KCB User] 팝업 객체:', window.userKcbPopup);
            
            if (!window.userKcbPopup) {
                // console.error('[KCB User] 팝업이 차단되었습니다');
                alert('팝업이 차단되었습니다. 팝업 차단을 해제해주세요.');
                return;
            }
            
            if (window.userKcbPopup.closed) {
                // console.error('[KCB User] 팝업이 즉시 닫혔습니다');
                alert('팝업이 차단되었습니다. 팝업 차단을 해제해주세요.');
                return;
            }
            
            // console.log('[KCB User] 팝업 열기 성공');
            
            // 팝업 로드 상태 확인
            const checkPopupStatus = setInterval(() => {
                if (window.userKcbPopup.closed) {
                    // console.log('[KCB User] 팝업이 닫혔습니다');
                    clearInterval(checkPopupStatus);
                } else {
                    try {
                        // 팝업이 로드되었는지 확인
                        if (window.userKcbPopup.location.href) {
                            // console.log('[KCB User] 팝업 URL:', window.userKcbPopup.location.href);
                            
                            // CertSelect 페이지가 로드되었는지 확인
                            if (window.userKcbPopup.location.href.includes('/auth/popup2')) {
                                // console.log('[KCB User] CertSelect 페이지 로드 완료');
                                clearInterval(checkPopupStatus);
                            }
                            
                            // 팝업이 실제 KCB 인증 페이지로 이동했는지 확인
                            if (window.userKcbPopup.location.href.includes('nice.checkplus.co.kr')) {
                                // console.log('[KCB User] KCB 인증 페이지로 이동 완료');
                                clearInterval(checkPopupStatus);
                            }
                        }
                    } catch (e) {
                        // CORS 에러는 정상적인 상황
                        // console.log('[KCB User] 팝업 로딩 중...');
                    }
                }
            }, 1000);
            
            // 30초 후 타임아웃
            setTimeout(() => {
                clearInterval(checkPopupStatus);
                // console.log('[KCB User] 팝업 로딩 타임아웃');
            }, 30000);
            
        } catch (error) {
            // console.error('[KCB User] 팝업 열기 실패:', error);
            alert('팝업을 열 수 없습니다: ' + error.message);
        }
    };

    // 이용약관 동의 관련 함수들
    const handleTermsAgreement = (type) => {
        if (type === 'all') {
            const newValue = !agreedTerms.allAgreed;
            setAgreedTerms({
                serviceTerms: newValue,
                privacyPolicy: newValue,
                refundPolicy: newValue,
                allAgreed: newValue
            });
        } else {
            const newAgreedTerms = {
                ...agreedTerms,
                [type]: !agreedTerms[type]
            };
            newAgreedTerms.allAgreed = newAgreedTerms.serviceTerms && newAgreedTerms.privacyPolicy && newAgreedTerms.refundPolicy;
            setAgreedTerms(newAgreedTerms);
        }
    };

    // 약관 모달에서 동의 버튼 클릭 시 호출되는 함수
    const handleTermsAgree = (termsType) => {
        let agreementType;
        switch (termsType) {
            case 'service':
                agreementType = 'serviceTerms';
                break;
            case 'privacy':
                agreementType = 'privacyPolicy';
                break;
            case 'refund':
                agreementType = 'refundPolicy';
                break;
            default:
                return;
        }
        
        const newAgreedTerms = {
            ...agreedTerms,
            [agreementType]: true
        };
        newAgreedTerms.allAgreed = newAgreedTerms.serviceTerms && newAgreedTerms.privacyPolicy && newAgreedTerms.refundPolicy;
        setAgreedTerms(newAgreedTerms);
    };

    const openTermsModal = (type) => {
        setModalType(type);
        setIsModalOpen(true);
    };

    const closeTermsModal = () => {
        setIsModalOpen(false);
        setModalType('');
    };

    // 서버가 "연결할 사용자 계정을 골라라" 라고 돌려준 두 분기(AMBIGUOUS_DEPENDENT · INVALID_DEPENDENT_SELECTION)가
    // 같이 쓴다. flushSync 로 후보 목록 · 오류를 먼저 DOM 에 반영한 뒤 focusFirstError 를 불러야 한다 —
    // 안 그러면(React 18 자동 배치) 계정 후보 상자(연결할 사용자 계정 라디오)가 아직 안 그려진 채로
    // focusFirstError 가 돌아 첫 서버 응답에서는 포커스가 조용히 비게 된다.
    const showAccountError = (dependentIndex, getCandidates, message) => {
        const key = `user_${dependentIndex}_account`;
        flushSync(() => {
            setUsers(previousUsers => previousUsers.map((user, index) =>
                index === dependentIndex
                    ? { ...user, accountCandidates: getCandidates(user), selectedAppUserId: null }
                    : user
            ));
            replaceErrors({ [key]: message });
        });
        focusFirstError({ [key]: message });
    };

    // 보호자 회원가입 제출 핸들러
    const handleGuardianSubmit = async () => {
        // spec D7: 입력칸에 관한 안내는 그 칸 아래로. 보호자 칸을 모두 검사해 한 번에 보이고,
        // 보호자 칸이 통과해야 사용자 칸을 본다(옛 순서와 같다).
        const guardianErrors = {};
        if (!guardianInfo.username) guardianErrors.guardian_username = '아이디를 입력해주세요.';
        if (!guardianInfo.password) guardianErrors.guardian_password = '비밀번호를 입력해주세요.';
        if (!guardianInfo.name) guardianErrors.guardian_name = '보호자 이름을 입력해주세요.';
        if (!guardianInfo.mobile) guardianErrors.guardian_mobile = '보호자 연락처를 입력해주세요.';
        if (!guardianInfo.gender) guardianErrors.guardian_gender = '보호자의 성별을 선택해주세요.';
        if (guardianInfo.password && guardianInfo.confirmPassword !== guardianInfo.password) {
            guardianErrors.guardian_confirmPassword = '비밀번호가 일치하지 않습니다.';
        }
        if (guardianInfo.name && guardianInfo.mobile && !guardianAuthResult) {
            guardianErrors.guardian_auth = '보호자 본인 인증을 진행해주세요.';
        }
        if (Object.keys(guardianErrors).length > 0) {
            replaceErrors(guardianErrors);
            focusFirstError(guardianErrors);
            return;
        }

        // 사용자 정보 검증
        const validUsers = users
            .map((user, formIndex) => ({ ...user, formIndex }))
            .filter(user => user.name && user.mobile && user.gender && user.authResult);

        const userErrors = {};
        users.forEach((user, idx) => {
            if (user.name && user.mobile && !user.gender) userErrors[`user_${idx}_gender`] = '성별을 선택해주세요.';
        });
        if (Object.keys(userErrors).length === 0 && validUsers.length === 0) {
            userErrors.user_0 = '최소 1명의 사용자 정보와 본인 인증을 완료해주세요.';
        }
        if (Object.keys(userErrors).length === 0) {
            const unresolvedUser = validUsers.find(user => user.accountCandidates?.length > 0 && !user.selectedAppUserId);
            if (unresolvedUser) {
                userErrors[`user_${unresolvedUser.formIndex}_account`] = `${unresolvedUser.name}님과 연결할 사용자 계정을 선택해주세요.`;
            }
        }
        if (Object.keys(userErrors).length > 0) {
            replaceErrors(userErrors);
            focusFirstError(userErrors);
            return;
        }
        replaceErrors({});

        try {
            // 보호자 비밀번호만 SHA-256 해시 (단방향)
            const guardianEncryptedPassword = await encryptPassword(guardianInfo.password);
            
            // 보호자 ECI값 처리 (일반 텍스트로 저장)
            let guardianProcessedECI = null;
            // console.log('보호자 인증 결과:', guardianAuthResult);
            if (guardianAuthResult && guardianAuthResult.eci) {
                guardianProcessedECI = await processECI(guardianAuthResult.eci);
            } else {
                // console.error('보호자 ECI 값이 없습니다:', guardianAuthResult);
                alert('보호자 본인 인증 정보가 올바르지 않습니다. 다시 인증해주세요.');
                return;
            }

            // 사용자들 정보 처리 (ECI 일반 텍스트로 저장)
            const usersWithData = await Promise.all(validUsers.map(async (user) => {
                let userProcessedECI = null;
                // console.log('사용자 인증 결과:', user.authResult);
                if (user.authResult && user.authResult.eci) {
                    userProcessedECI = await processECI(user.authResult.eci);
                } else {
                    // console.error('사용자 ECI 값이 없습니다:', user.authResult);
                    alert('사용자 본인 인증 정보가 올바르지 않습니다. 다시 인증해주세요.');
                    return;
                }
                
                return {
                    name: user.name,                    // 원본 값 (서버에서 암호화)
                    mobile: user.mobile,                // 원본 값 (서버에서 암호화)
                    birthdate: user.birthdate,          // 생년월일 추가
                    gender: user.gender,                // 원본 값 (서버에서 암호화)
                    eci: userProcessedECI,              // 일반 텍스트
                    app_user_id: user.selectedAppUserId || undefined,
                    client_index: user.formIndex
                };
            }));

            const response = await axios.post(`${API_BASE_URL}/api/guardian-signup`, {
                guardian: {
                    username: guardianInfo.username,    // 원본 값 (서버에서 암호화)
                    password: guardianEncryptedPassword, // SHA-256 해시
                    name: guardianInfo.name,            // 원본 값 (서버에서 암호화)
                    mobile: guardianInfo.mobile,        // 원본 값 (서버에서 암호화)
                    birthdate: guardianInfo.birthdate,  // 생년월일 추가
                    gender: guardianInfo.gender,        // 원본 값 (서버에서 암호화)
                    eci: guardianProcessedECI           // 일반 텍스트
                },
                users: usersWithData
            });

            if (response.data.success) {
                alert('보호자 회원가입이 완료되었습니다.');
                navigate('/guardian'); // 보호자 로그인 페이지 (/login 은 주치의 로그인)
            }
        } catch (error) {
            // console.error('보호자 회원가입 실패:', error);
            const responseData = error.response?.data;
            if (responseData?.code === 'AMBIGUOUS_DEPENDENT'
                && Array.isArray(responseData.candidates)
                && Number.isInteger(responseData.dependent_index)) {
                showAccountError(
                    responseData.dependent_index,
                    () => responseData.candidates,
                    responseData.error || '연결할 사용자 계정을 선택해주세요.'
                );
            } else if (responseData?.code === 'INVALID_DEPENDENT_SELECTION'
                && Number.isInteger(responseData.dependent_index)) {
                showAccountError(
                    responseData.dependent_index,
                    (user) => responseData.candidates || user.accountCandidates,
                    responseData.error || '사용자 계정을 다시 선택해주세요.'
                );
            } else if (responseData?.error) {
                alert(responseData.error);
            } else if (error.message.includes('암호화')) {
                alert('암호화 중 오류가 발생했습니다. 다시 시도해주세요.');
            } else {
                alert('보호자 회원가입 중 오류가 발생했습니다.');
            }
        }
    };



    return (
        tokenInUrl ? (
            // 메일함의 인증 링크로 열린 창. 인증 결과만 알리고 회원가입 폼은 그리지 않는다.
            // 메일 인증은 주치의 회원가입에만 있다.
            <AuthLayout title="이메일 인증" eyebrow="주치의 회원가입">
                {isVerifyingToken || (!isEmailVerified && !emailCheckResult) ? (
                    <div className="auth-page__relay" role="status">
                        <span className="auth-page__spinner" aria-hidden="true" />
                        <p className="auth-page__desc">이메일 인증을 확인하고 있어요...</p>
                    </div>
                ) : isEmailVerified ? (
                    <div className="auth-page__relay" role="status">
                        <h2 className="auth-page__section-title">이메일 인증이 완료되었습니다.</h2>
                        <p className="auth-page__desc">이 창을 닫고, 기존 회원가입 창에서 회원가입을 계속 진행해 주세요.</p>
                        <button type="button" className="sds-btn sds-btn--primary" onClick={() => window.close()}>완료</button>
                    </div>
                ) : (
                    <div className="auth-page__relay" role="alert">
                        <h2 className="auth-page__section-title">이메일 인증을 완료하지 못했어요.</h2>
                        <p className="auth-page__desc">{emailCheckResult}</p>
                        <p className="auth-page__desc">기존 회원가입 창에서 "인증 메일 다시 보내기"를 눌러 새 링크를 받아 주세요.</p>
                        <button type="button" className="sds-btn sds-btn--secondary" onClick={() => window.close()}>닫기</button>
                    </div>
                )}
            </AuthLayout>
        ) : (
        <AuthLayout
            wide
            title="회원가입"
            description={signupType === 'doctor'
                ? '메일 인증과 본인인증을 마치면 소속 병원을 골라 가입할 수 있어요.'
                : '보호자와 돌볼 사용자 모두 본인인증이 필요해요. 사용자는 여러 명 추가할 수 있어요.'}
            eyebrow={signupType === 'doctor' ? '주치의 회원가입' : '보호자 회원가입'}
        >
            {signupType === 'doctor' ? (
                <form onSubmit={handleSubmit} className="auth-page__form" onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}>
                    {isVerifyingToken && (
                        <div className="sds-notice sds-notice--info" role="status">
                            <span className="auth-page__spinner" aria-hidden="true" />이메일 인증을 확인하고 있습니다...
                        </div>
                    )}

                    <section className="auth-page__section" aria-labelledby="signup-account">
                        <h2 id="signup-account" className="auth-page__section-title">계정</h2>
                        <div className="sds-field">
                            <label className="sds-label" htmlFor="signup-name">이름<span className="sds-required">*</span></label>
                            <input id="signup-name" className="sds-input" type="text" name="user_name" placeholder="이름"
                                value={formData.user_name} onChange={handleInputChange} required disabled={isVerifyingToken} />
                        </div>
                        <div className="sds-field">
                            <label className="sds-label" htmlFor="signup-email">이메일<span className="sds-required">*</span></label>
                            <div className="auth-page__row">
                                <input id="signup-email" className={`sds-input${errors.user_email ? ' sds-input--error' : ''}`} type="email" name="user_email" placeholder="이메일"
                                    value={formData.user_email} onChange={handleInputChange} required autoComplete="username"
                                    readOnly={isEmailVerified} disabled={isVerifyingToken} {...fieldProps('user_email')} />
                                {!isEmailVerified && !isVerifyingToken && !isWaitingForVerification && (
                                    <button type="button" className="sds-btn sds-btn--secondary" onClick={handleEmailVerification}
                                        disabled={isEmailChecking || isSendingVerification || !formData.user_email}>
                                        {isEmailChecking ? '검증 중...' : isSendingVerification ? '메일 보내는 중...' : '메일 검증'}
                                    </button>
                                )}
                            </div>
                            <FieldError field="user_email" errors={errors} />
                            {emailCheckResult && (
                                <p className={isEmailValid ? 'auth-page__ok' : 'sds-field-error'} role="status">{emailCheckResult}</p>
                            )}
                            {isWaitingForVerification && !isEmailVerified && !isVerifyingToken && (
                                <div className="auth-page__row">
                                    <p className="auth-page__hint">메일함에서 인증 링크를 누르면 이 화면이 저절로 다음 단계로 넘어가요. 메일이 보이지 않으면 스팸함도 확인해 주세요.</p>
                                    <button type="button" className="sds-btn sds-btn--weak sds-btn--sm" onClick={handleSendVerificationEmail} disabled={isSendingVerification}>
                                        {isSendingVerification ? '보내는 중...' : '인증 메일 다시 보내기'}
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="sds-field">
                            <label className="sds-label" htmlFor="signup-password">비밀번호<span className="sds-required">*</span></label>
                            <input id="signup-password" className="sds-input" type="password" name="user_pw" placeholder="비밀번호"
                                value={formData.user_pw} onChange={handleInputChange} required autoComplete="current-password" disabled={isVerifyingToken} />
                        </div>
                        <div className="sds-field">
                            <label className="sds-label" htmlFor="signup-confirm">비밀번호 확인<span className="sds-required">*</span></label>
                            <input id="signup-confirm" className={`sds-input${errors.confirmPassword ? ' sds-input--error' : ''}`} type="password" name="confirmPassword" placeholder="비밀번호 확인"
                                value={formData.confirmPassword} onChange={handleInputChange} required autoComplete="new-password" disabled={isVerifyingToken}
                                {...fieldProps('confirmPassword')} />
                            <FieldError field="confirmPassword" errors={errors} />
                        </div>
                    </section>

                    <section className="auth-page__section" aria-labelledby="signup-contact">
                        <h2 id="signup-contact" className="auth-page__section-title">연락처 · 본인인증</h2>
                        <div className="sds-field">
                            <label className="sds-label" htmlFor="signup-mobile">연락처<span className="sds-required">*</span></label>
                            <div className="auth-page__row">
                                <input id="signup-mobile" className="sds-input" type="tel" name="user_mobile" placeholder="연락처 (예: 010-1234-5678)"
                                    value={formData.user_mobile} onChange={handleInputChange} required pattern="^\d{3}-\d{3,4}-\d{4}$"
                                    onInvalid={e => e.target.setCustomValidity('요청한 형식과 일치시키세요. (예 : 010-1234-5678)')}
                                    onInput={e => e.target.setCustomValidity('')} disabled={isVerifyingToken} />
                                <button type="button" className="sds-btn sds-btn--secondary" onClick={openKcbCertPopup} disabled={isVerifyingToken}>본인인증</button>
                            </div>
                            {kcbAuthResult && <p className="auth-page__ok" role="status">본인인증 완료: {kcbAuthResult.name} ({kcbAuthResult.tel_no})</p>}
                        </div>
                    </section>

                    <section className="auth-page__section" aria-labelledby="signup-hospital">
                        <h2 id="signup-hospital" className="auth-page__section-title">소속 병원</h2>
                        <div className="sds-field">
                            <span className="sds-label" id="signup-region-label">지역<span className="sds-required">*</span></span>
                            <div className="auth-page__row" data-field="region">
                                <select aria-label="시/도" className={`sds-select${errors.region ? ' sds-select--error' : ''}`} value={selectedRegion} onChange={handleRegionChange} required disabled={isVerifyingToken}
                                    aria-invalid={errors.region ? 'true' : undefined} aria-describedby={errors.region ? 'region-error' : undefined}>
                                    <option value="">시/도 선택</option>
                                    {regions.map((region) => <option key={region.region_code} value={region.region_name}>{region.region_name}</option>)}
                                </select>
                                <select aria-label="구/군" className="sds-select" value={selectedDistrict} onChange={handleDistrictChange} disabled={!selectedRegion || isVerifyingToken}>
                                    <option value="">전체 구/군</option>
                                    {districts.map((district) => <option key={district.district_code} value={district.district_name}>{district.district_name}</option>)}
                                </select>
                            </div>
                            <FieldError field="region" errors={errors} />
                        </div>
                        <div className="sds-field">
                            <label className="sds-label" htmlFor="signup-hospital-search">병원 검색<span className="sds-required">*</span></label>
                            <div className="auth-page__row">
                                <input id="signup-hospital-search" className="sds-input" type="text" value={hospitalSearchTerm}
                                    onChange={(e) => setHospitalSearchTerm(e.target.value)} placeholder="병원명을 입력하세요" disabled={isVerifyingToken} />
                                <button type="button" className="sds-btn sds-btn--secondary" onClick={handleHospitalSearch} disabled={!selectedRegion || isVerifyingToken}>검색</button>
                            </div>
                            {hospitalNotice && <p className="auth-page__hint" role="status">{hospitalNotice}</p>}
                            {hospitalSearchResults.length > 0 && (
                                <ul className="auth-page__results">
                                    {hospitalSearchResults.map((hospital) => {
                                        const selected = selectedHospital?.id === hospital.id;
                                        return (
                                            <li key={hospital.id}>
                                                <button type="button" className={`auth-page__result${selected ? ' is-selected' : ''}`} aria-pressed={selected}
                                                    onClick={() => !isVerifyingToken && handleHospitalSelect(hospital)}>
                                                    <span className="sds-badge sds-badge--none">{hospital.type_name}</span>
                                                    <b>{hospital.institution_name}</b>
                                                    <small>{hospital.address}</small>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </section>

                    <section className="auth-page__section" aria-labelledby="signup-terms">
                        <h2 id="signup-terms" className="auth-page__section-title">이용약관 동의<span className="sds-required">*</span></h2>
                        <TermsChecks agreedTerms={agreedTerms} onToggle={handleTermsAgreement} onOpen={openTermsModal} disabled={isVerifyingToken} serviceLabel="이용약관에 동의합니다" />
                    </section>

                    <div className="auth-page__actions">
                        <button type="button" className="sds-btn sds-btn--secondary" onClick={() => navigate('/doctor')} disabled={isVerifyingToken}>뒤로가기</button>
                        <button type="submit" className="sds-btn sds-btn--primary" disabled={!selectedHospital || isVerifyingToken || !isEmailVerified || !agreedTerms.allAgreed}>회원가입</button>
                    </div>
                    <p className="auth-page__hint"><span className="sds-required">*</span> 필수 입력 항목입니다.</p>
                </form>
            ) : (
                    <form onSubmit={(e) => e.preventDefault()} className="auth-page__form">
                        <fieldset className={`auth-page__person${guardianAuthResult ? ' is-verified' : ''}`}>
                            <legend className="auth-page__person-head">
                                보호자 정보
                                <span className={`sds-badge sds-badge--${guardianAuthResult ? 'normal' : 'none'}`}>{guardianAuthResult ? '인증 완료' : '인증 필요'}</span>
                            </legend>
                            <div className="auth-page__grid">
                                <div className="sds-field">
                                    <label className="sds-label" htmlFor="guardian-username">아이디<span className="sds-required">*</span></label>
                                    <input id="guardian-username" className={`sds-input${errors.guardian_username ? ' sds-input--error' : ''}`} type="text" name="guardian_username" placeholder="아이디"
                                        value={guardianInfo.username}
                                        onChange={e => { setGuardianInfo({ ...guardianInfo, username: e.target.value }); clearError('guardian_username'); }}
                                        required autoComplete="username" {...fieldProps('guardian_username')} />
                                    <FieldError field="guardian_username" errors={errors} />
                                </div>
                                <div className="sds-field">
                                    <label className="sds-label" htmlFor="guardian-gender">보호자 성별<span className="sds-required">*</span></label>
                                    <select id="guardian-gender" className={`sds-select${errors.guardian_gender ? ' sds-select--error' : ''}`} name="guardian_gender"
                                        value={guardianInfo.gender}
                                        onChange={e => { setGuardianInfo({ ...guardianInfo, gender: e.target.value }); clearError('guardian_gender'); }}
                                        required {...fieldProps('guardian_gender')}>
                                        <option value="">성별 선택</option>
                                        <option value="M">남성</option>
                                        <option value="F">여성</option>
                                    </select>
                                    <FieldError field="guardian_gender" errors={errors} />
                                </div>
                                <div className="sds-field">
                                    <label className="sds-label" htmlFor="guardian-password">비밀번호<span className="sds-required">*</span></label>
                                    <input id="guardian-password" className={`sds-input${errors.guardian_password ? ' sds-input--error' : ''}`} type="password" name="guardian_password" placeholder="비밀번호"
                                        value={guardianInfo.password}
                                        onChange={e => { setGuardianInfo({ ...guardianInfo, password: e.target.value }); clearError('guardian_password'); clearError('guardian_confirmPassword'); }}
                                        required autoComplete="new-password" {...fieldProps('guardian_password')} />
                                    <FieldError field="guardian_password" errors={errors} />
                                </div>
                                <div className="sds-field">
                                    <label className="sds-label" htmlFor="guardian-confirm">비밀번호 확인<span className="sds-required">*</span></label>
                                    <input id="guardian-confirm" className={`sds-input${errors.guardian_confirmPassword ? ' sds-input--error' : ''}`} type="password" name="guardian_confirmPassword" placeholder="비밀번호 확인"
                                        value={guardianInfo.confirmPassword}
                                        onChange={e => { setGuardianInfo({ ...guardianInfo, confirmPassword: e.target.value }); clearError('guardian_confirmPassword'); }}
                                        required autoComplete="new-password" {...fieldProps('guardian_confirmPassword')} />
                                    <FieldError field="guardian_confirmPassword" errors={errors} />
                                </div>
                                <div className="sds-field">
                                    <label className="sds-label" htmlFor="guardian-name">보호자 이름<span className="sds-required">*</span></label>
                                    <input id="guardian-name" className={`sds-input${errors.guardian_name ? ' sds-input--error' : ''}`} type="text" name="guardian_name" placeholder="보호자 이름"
                                        value={guardianInfo.name}
                                        onChange={e => { setGuardianInfo({ ...guardianInfo, name: e.target.value }); clearError('guardian_name'); clearError('guardian_auth'); }}
                                        required disabled={!!guardianAuthResult} {...fieldProps('guardian_name')} />
                                    <FieldError field="guardian_name" errors={errors} />
                                </div>
                                <div className="sds-field">
                                    <label className="sds-label" htmlFor="guardian-mobile">보호자 연락처<span className="sds-required">*</span></label>
                                    <input id="guardian-mobile" className={`sds-input${errors.guardian_mobile ? ' sds-input--error' : ''}`} type="tel" name="guardian_mobile" placeholder="보호자 연락처"
                                        value={guardianInfo.mobile}
                                        onChange={e => { setGuardianInfo({ ...guardianInfo, mobile: e.target.value }); clearError('guardian_mobile'); clearError('guardian_auth'); }}
                                        required disabled={!!guardianAuthResult} {...fieldProps('guardian_mobile')} />
                                    <FieldError field="guardian_mobile" errors={errors} />
                                </div>
                            </div>
                            <div className="auth-page__row" data-field="guardian_auth">
                                <button type="button" className="sds-btn sds-btn--secondary" onClick={openGuardianKcbCertPopup} disabled={!!guardianAuthResult}>보호자 본인인증</button>
                                {guardianAuthResult && <p className="auth-page__ok" role="status">보호자 인증 완료: {guardianAuthResult.name} ({guardianAuthResult.mobile})</p>}
                            </div>
                            <FieldError field="guardian_auth" errors={errors} />
                        </fieldset>

                        <section className="auth-page__section" aria-labelledby="signup-users">
                            <h2 id="signup-users" className="auth-page__section-title">사용자 정보<span className="sds-required">*</span></h2>
                            {users.map((user, idx) => (
                                <fieldset key={idx} className={`auth-page__person${user.authResult ? ' is-verified' : ''}`} data-field={idx === 0 ? 'user_0' : undefined}>
                                    <legend className="auth-page__person-head">
                                        사용자 {idx + 1}
                                        <span className={`sds-badge sds-badge--${user.authResult ? 'normal' : 'none'}`}>{user.authResult ? '인증 완료' : '인증 필요'}</span>
                                    </legend>
                                    <div className="auth-page__grid auth-page__grid--user">
                                        <input type="text" placeholder="사용자 이름" aria-label="사용자 이름" className="sds-input"
                                            value={user.name}
                                            onChange={e => {
                                                const newUsers = [...users];
                                                newUsers[idx].name = e.target.value;
                                                setUsers(newUsers);
                                                clearError(`user_${idx}_gender`); clearError('user_0');
                                            }}
                                            required disabled={!!user.authResult} />
                                        <input type="tel" placeholder="사용자 연락처" aria-label="사용자 연락처" className="sds-input"
                                            value={user.mobile}
                                            onChange={e => {
                                                const newUsers = [...users];
                                                newUsers[idx].mobile = e.target.value;
                                                setUsers(newUsers);
                                                clearError(`user_${idx}_gender`); clearError('user_0');
                                            }}
                                            required disabled={!!user.authResult} />
                                        <select aria-label="성별" className={`sds-select${errors[`user_${idx}_gender`] ? ' sds-select--error' : ''}`}
                                            value={user.gender}
                                            onChange={e => {
                                                const newUsers = [...users];
                                                newUsers[idx].gender = e.target.value;
                                                setUsers(newUsers);
                                                clearError(`user_${idx}_gender`); clearError('user_0');
                                            }}
                                            required {...fieldProps(`user_${idx}_gender`)}>
                                            <option value="">성별</option>
                                            <option value="M">남성</option>
                                            <option value="F">여성</option>
                                        </select>
                                        <button type="button" className="sds-btn sds-btn--secondary" onClick={() => openUserKcbCertPopup(idx)} disabled={!!user.authResult}>사용자 본인인증</button>
                                    </div>
                                    <FieldError field={`user_${idx}_gender`} errors={errors} />
                                    {user.authResult && <p className="auth-page__ok" role="status">사용자 인증 완료: {user.authResult.name} ({user.authResult.mobile})</p>}
                                    {user.accountCandidates?.length > 0 && (
                                        <div className="auth-page__accounts" role="radiogroup" aria-label="연결할 사용자 계정" data-field={`user_${idx}_account`}>
                                            <p className="auth-page__hint">연결할 사용자 계정을 선택해주세요.</p>
                                            {user.accountCandidates.map(candidate => (
                                                <label key={candidate.app_user_id} className="auth-page__check">
                                                    <input type="radio" name={`dependent-account-${idx}`} checked={user.selectedAppUserId === candidate.app_user_id}
                                                        onChange={() => {
                                                            setUsers(previousUsers => previousUsers.map((item, itemIndex) =>
                                                                itemIndex === idx
                                                                    ? { ...item, selectedAppUserId: candidate.app_user_id }
                                                                    : item
                                                            ));
                                                            clearError(`user_${idx}_account`);
                                                        }} />
                                                    <span>사용자 ID {candidate.app_user_id}</span>
                                                    <small className="auth-page__hint">건강관리 ID {candidate.management_id || '없음'}</small>
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                    <FieldError field={`user_${idx}_account`} errors={errors} />
                                    {idx === 0 && <FieldError field="user_0" errors={errors} />}
                                </fieldset>
                            ))}
                            <button type="button" className="sds-btn sds-btn--weak" onClick={() => setUsers([...users, { name: '', mobile: '', gender: '', authResult: null }])}>
                                <Plus size={16} aria-hidden="true" />사용자 추가
                            </button>
                        </section>

                        <section className="auth-page__section" aria-labelledby="signup-terms-guardian">
                            <h2 id="signup-terms-guardian" className="auth-page__section-title">이용약관 동의<span className="sds-required">*</span></h2>
                            <TermsChecks agreedTerms={agreedTerms} onToggle={handleTermsAgreement} onOpen={openTermsModal} serviceLabel="이용약관 동의" />
                        </section>

                        <div className="auth-page__actions">
                            <button type="button" className="sds-btn sds-btn--secondary" onClick={() => navigate('/guardian')}>뒤로가기</button>
                            <button type="button" className="sds-btn sds-btn--primary" onClick={handleGuardianSubmit} disabled={!agreedTerms.allAgreed}>회원가입</button>
                        </div>
                    </form>
                )}
            <TermsModal
                isOpen={isModalOpen}
                onClose={closeTermsModal}
                type={modalType}
                onAgree={handleTermsAgree}
            />
        </AuthLayout>
        )
    );
};

// 약관 동의 네 칸. 주치의 · 보호자 폼이 같이 쓴다(첫 줄 문구만 다르다).
function TermsChecks({ agreedTerms, onToggle, onOpen, disabled, serviceLabel }) {
    const rows = [
        ['serviceTerms', serviceLabel, 'service'],
        ['privacyPolicy', '개인정보 수집 및 이용 동의', 'privacy'],
        ['refundPolicy', '환불 및 반품 정책 동의', 'refund']
    ];
    return (
        <div className="auth-page__terms">
            <label className="auth-page__check auth-page__check--all">
                <input type="checkbox" checked={agreedTerms.allAgreed} onChange={() => onToggle('all')} disabled={disabled} />
                <span>모든 약관에 동의합니다</span>
            </label>
            {rows.map(([key, label, modal]) => (
                <div className="auth-page__check-row" key={key}>
                    <label className="auth-page__check">
                        <input type="checkbox" checked={agreedTerms[key]} onChange={() => onToggle(key)} disabled={disabled} />
                        <span>{label}</span>
                    </label>
                    <button type="button" className="auth-page__textlink" onClick={() => onOpen(modal)} aria-label={`${label} 보기`}>보기</button>
                </div>
            ))}
        </div>
    );
}

export default Signup;
